import { useState, useRef } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon, Loader2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

interface InlineDateFieldProps {
  value: string | null;
  onSave: (value: string | null) => Promise<void>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  testId?: string;
}

type SaveState = "idle" | "saving" | "success" | "error";

export function InlineDateField({
  value,
  onSave,
  placeholder = "Pick a date...",
  className,
  disabled = false,
  label,
  testId,
}: InlineDateFieldProps) {
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const handleDateSelect = async (date: Date | undefined) => {
    setOpen(false);

    const newValue = date ? format(date, "yyyy-MM-dd") : null;

    if (newValue === value) {
      return;
    }

    setSaveState("saving");

    try {
      await onSave(newValue);
      setSaveState("success");

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 2000);
    } catch {
      setSaveState("error");

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 3000);
    }
  };

  const handleKeyboardEntry = (inputDate: string) => {
    setInputError(false);

    // Parse DD/MM/YYYY format
    const parsedDate = parse(inputDate, "dd/MM/yyyy", new Date());

    if (isValid(parsedDate)) {
      handleDateSelect(parsedDate);
      setInputValue("");
    } else {
      setInputError(true);
      setTimeout(() => setInputError(false), 3000);
    }
  };

  const SaveIndicator = () => {
    if (saveState === "saving") {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    if (saveState === "success") {
      return <Check className="h-4 w-4 text-green-600 dark:text-green-400" />;
    }
    if (saveState === "error" || inputError) {
      return <X className="h-4 w-4 text-destructive" />;
    }
    return null;
  };

  const dateValue = value ? new Date(value) : undefined;
  // Changed format from PPP to dd/MM/yyyy
  const displayValue = value ? format(new Date(value), "dd/MM/yyyy") : null;

  return (
    <div className="space-y-1">
      {label && (
        <label className="text-sm font-medium text-muted-foreground">
          {label}
        </label>
      )}
      <div className="relative flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "flex-1 justify-start text-left font-normal",
                !value && "text-muted-foreground",
                className
              )}
              disabled={disabled || saveState === "saving"}
              data-testid={testId || "inline-date-field"}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {displayValue || placeholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="DD/MM/YYYY"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && inputValue) {
                    handleKeyboardEntry(inputValue);
                  }
                }}
                className={cn(
                  "w-full",
                  inputError && "border-destructive"
                )}
              />
              <Calendar
                mode="single"
                selected={dateValue}
                onSelect={handleDateSelect}
                initialFocus
                captionLayout="dropdown"
                fromYear={1900}
                toYear={2100}
              />
            </div>
          </PopoverContent>
        </Popover>
        <div className="w-4 flex items-center justify-center">
          <SaveIndicator />
        </div>
      </div>
    </div>
  );
}
